// 承攬合約 PDF 生成工具
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// 型別定義
export interface PartnerContractData {
  contract_number: string;
  contract_title: string;
  contract_type: 'service' | 'consulting' | 'development';
  contract_terms?: string;
  start_date?: string;
  end_date?: string;
  hourly_rate?: number;
  service_types?: Array<{ name: string; hourly_rate?: number; unit?: '小時' | '計次' }> | string[];
  // 承攬夥伴資訊
  partner_name: string;
  partner_english_name?: string;
  partner_id_number?: string;
  partner_address?: string;
  partner_email?: string;
  partner_phone?: string;
  // 銀行資訊
  bank_account_name?: string;
  bank_name?: string;
  bank_branch?: string;
  bank_account_number?: string;
  // 專案資訊
  project_name: string;
  customer_company?: string;
  work_description?: string;
  // 日期
  contract_date: string;
  // 甲方大小章
  company_stamp?: string;
}

// 載入合約模板
async function loadContractTemplate(): Promise<string> {
  try {
    const response = await fetch('/templates/partnerContractTemplate.html');
    if (!response.ok) {
      throw new Error(`無法載入模板: ${response.statusText}`);
    }
    return await response.text();
  } catch (error) {
    throw new Error(`載入模板失敗: ${error}`);
  }
}

// 將西元年轉換為民國年完整日期格式
function toROCDateFull(dateString: string): string {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const rocYear = year - 1911;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${rocYear}年${month}月${day}日`;
  } catch {
    return dateString;
  }
}

// 生成合約 HTML
export async function generateContractHtml(contract: PartnerContractData): Promise<string> {
  const template = await loadContractTemplate();
  
  // 轉換日期格式（民國年完整日期）
  const startDateROC = contract.start_date ? toROCDateFull(contract.start_date) : '';
  const endDateROC = contract.end_date ? toROCDateFull(contract.end_date) : '';
  
  // 確保標題格式一致
  const contractTitle = contract.contract_title || '遠程資科有限公司承攬契約書';
  
  // 處理服務技能顯示（包含單價和計價方式）
  let serviceTypesHtml = '';
  if (contract.service_types && Array.isArray(contract.service_types) && contract.service_types.length > 0) {
    serviceTypesHtml = contract.service_types.map((skill: unknown) => {
      if (typeof skill === 'string') {
        return `<li>${skill}</li>`;
      } else if (typeof skill === 'object' && skill !== null) {
        const skillObj = skill as { name?: string; hourly_rate?: number; unit?: '小時' | '計次' };
        const skillName = skillObj.name || '';
        const hourlyRate = skillObj.hourly_rate ? skillObj.hourly_rate.toLocaleString() : '';
        const unit = skillObj.unit || '小時';
        if (hourlyRate) {
          return `<li>${skillName}：${hourlyRate} 元/${unit}</li>`;
        } else {
          return `<li>${skillName}</li>`;
        }
      }
      return '';
    }).filter(Boolean).join('');
  } else {
    serviceTypesHtml = '<li>（工作內容詳見專案說明）</li>';
  }

  // 替換變數
  const html = template
    .replace(/\{\{CONTRACT_NUMBER\}\}/g, contract.contract_number)
    .replace(/\{\{CONTRACT_TITLE\}\}/g, contractTitle)
    .replace(/\{\{PARTNER_NAME\}\}/g, contract.partner_name)
    .replace(/\{\{PARTNER_ENGLISH_NAME\}\}/g, contract.partner_english_name || '')
    .replace(/\{\{PARTNER_ID_NUMBER\}\}/g, contract.partner_id_number || '')
    .replace(/\{\{PARTNER_ADDRESS\}\}/g, contract.partner_address || '')
    .replace(/\{\{PARTNER_EMAIL\}\}/g, contract.partner_email || '')
    .replace(/\{\{PARTNER_PHONE\}\}/g, contract.partner_phone || '')
    .replace(/\{\{PROJECT_NAME\}\}/g, contract.project_name)
    .replace(/\{\{CUSTOMER_COMPANY\}\}/g, contract.customer_company || '')
    .replace(/\{\{START_DATE_ROC\}\}/g, startDateROC)
    .replace(/\{\{END_DATE_ROC\}\}/g, endDateROC)
    .replace(/\{\{HOURLY_RATE\}\}/g, contract.hourly_rate ? contract.hourly_rate.toLocaleString() : '')
    .replace(/\{\{BANK_ACCOUNT_NAME\}\}/g, contract.bank_account_name || '')
    .replace(/\{\{BANK_NAME\}\}/g, contract.bank_name || '')
    .replace(/\{\{BANK_BRANCH\}\}/g, contract.bank_branch || '')
    .replace(/\{\{BANK_ACCOUNT_NUMBER\}\}/g, contract.bank_account_number || '')
    .replace(/\{\{WORK_DESCRIPTION\}\}/g, contract.work_description || contract.project_name || '')
    .replace(/\{\{SERVICE_TYPES\}\}/g, serviceTypesHtml)
    .replace(/\{\{CONTRACT_DATE\}\}/g, contract.contract_date)
    .replace(/\{\{COMPANY_STAMP\}\}/g, contract.company_stamp || '')
    .replace(/\{\{COMPANY_STAMP_DISPLAY\}\}/g, contract.company_stamp ? 'block' : 'none');
  
  return html;
}

// 添加頁首頁尾
function addPageHeaderFooter(pdf: jsPDF, pageNumber: number, totalPages: number, contract: PartnerContractData) {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  
  // 添加頁首文字
  pdf.setFontSize(10);
  pdf.setTextColor(128, 128, 128);
  const headerText = `Teleworker Inc. - 承攬合約 ${contract.contract_number}`;
  pdf.text(headerText, pdfWidth / 2, 15, { align: 'center' });
  
  // 添加頁尾文字
  const footerText = `Page ${pageNumber}/${totalPages}`;
  pdf.text(footerText, pdfWidth / 2, pdfHeight - 10, { align: 'center' });
  
  // 添加分隔線
  pdf.setDrawColor(238, 238, 238);
  pdf.line(10, 20, pdfWidth - 10, 20);
  pdf.line(10, pdfHeight - 15, pdfWidth - 10, pdfHeight - 15);
}

// 分頁生成 PDF 函數
async function generatePdfByPages(pdf: jsPDF, tempDiv: HTMLElement, contract: PartnerContractData) {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  
  // 將整個內容作為一個元素處理
  const canvas = await html2canvas(tempDiv, {
    scale: 1,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    width: 800,
    height: tempDiv.scrollHeight,
    scrollX: 0,
    scrollY: 0,
    imageTimeout: 0,
    logging: false
  });
  
  // 計算每頁可用內容高度（A4 高度 - 頁首頁尾空間）
  const availableHeight = pdfHeight - 40; // 留 20mm 給頁首，20mm 給頁尾
  
  // 計算圖片在 PDF 中的寬度（保持比例）
  const imgWidth = pdfWidth - 20; // 留 10mm 邊距
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  
  // 計算總頁數
  const totalPages = Math.ceil(imgHeight / availableHeight);
  
  // 分頁處理
  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
    // 為每頁添加頁首頁尾
    addPageHeaderFooter(pdf, pageNumber, totalPages, contract);
    
    // 計算當前頁面應該顯示的圖片部分
    const startY = (pageNumber - 1) * availableHeight;
    const endY = Math.min(pageNumber * availableHeight, imgHeight);
    const currentHeight = endY - startY;
    
    // 計算在 PDF 中的位置（留 20mm 給頁首）
    const pdfY = 20;
    
    // 計算截取部分
    const sourceY = (startY / imgHeight) * canvas.height;
    const sourceHeight = (currentHeight / imgHeight) * canvas.height;
    
    // 創建一個新的 canvas 來截取需要的部分
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = canvas.width;
    tempCanvas.height = sourceHeight;
    
    if (tempCtx) {
      tempCtx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
      const tempImgData = tempCanvas.toDataURL('image/jpeg', 0.8);
      
      // 計算截取部分的 PDF 尺寸
      const tempImgWidth = imgWidth;
      const tempImgHeight = (sourceHeight * imgWidth) / canvas.width;
      
      // 添加到 PDF
      pdf.addImage(tempImgData, 'JPEG', 10, pdfY, tempImgWidth, tempImgHeight);
    }
    
    if (pageNumber < totalPages) {
      pdf.addPage();
    }
  }
}

// 生成 PDF Blob
export async function generateContractPdfBlob(contract: PartnerContractData): Promise<Blob> {
  const html = await generateContractHtml(contract);
  
  try {
    // 創建一個臨時的 div 元素來放置 HTML 內容
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    tempDiv.style.width = '800px';
    document.body.appendChild(tempDiv);
    
    // 使用 jsPDF 生成 PDF
    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      compress: true
    });
    
    // 分頁生成 PDF
    await generatePdfByPages(pdf, tempDiv, contract);
    
    // 移除臨時元素
    document.body.removeChild(tempDiv);
    
    // 返回 PDF blob
    return pdf.output('blob');
  } catch (error) {
    throw new Error(`PDF 產生失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
  }
}

// 下載 PDF
export async function downloadContractPdf(contract: PartnerContractData, fileName?: string) {
  const blob = await generateContractPdfBlob(contract);
  const defaultFileName = `承攬合約_${contract.contract_number}.pdf`;
  saveAs(blob, fileName || defaultFileName);
}

// 將 PDF 轉換為 Base64（用於上傳）
export async function generateContractPdfBase64(contract: PartnerContractData): Promise<string> {
  const blob = await generateContractPdfBlob(contract);
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      // 移除 data:application/pdf;base64, 前綴
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

